/* eslint-disable global-require */
export interface ProductivityTip {
  id: number;
  title: string;
  shortDescription: string;
  expandedDescription: string;
  image: string;
}

/* eslint-disable @typescript-eslint/no-var-requires */
export const PRODUCTIVITY_TIPS: ProductivityTip[] = [
  {
    id: 1,
    title: 'Your dashboard is customizable',
    shortDescription: 'Add, remove, re-order, or change what\'s shown on each widget.',
    expandedDescription:
      'Click on the \'settings\' icon on each widget to explore your customization options: filter the data by a specific timeframe, select which metrics are displayed, or even hide the entire widget.',
    image: require('@/assets/images/tips/tip-image-1.png'),
  },
  {
    id: 2,
    title: 'Dashboard layout is fully flexible',
    shortDescription: 'Reposition any widget on your dashboard with a simple drag-and-drop.',
    expandedDescription:
      'Simply drag-and-drop any widget to reposition it on the dashboard. That way, your layout reflects your priorities and your most important data stays exactly where you expect it.',
    image: require('@/assets/images/tips/tip-image-2.png'),
  },
  {
    id: 3,
    title: 'Add your own carrier accounts',
    shortDescription: 'Connect your carrier accounts to ship at your own negotiated rates.',
    expandedDescription:
      'Enter your carrier account details in the Carrier Accounts section on the Connect page. This way, you can ship with your preferred carriers at your own rates, without giving up the platform\'s features and discounted rates.',
    image: require('@/assets/images/tips/tip-image-3.png'),
  },
  {
    id: 4,
    title: 'Accurate carrier cutoff times',
    shortDescription: 'See daily carrier cutoff times based on your postal code.',
    expandedDescription:
      'Navigate to the Cutoff Times section on the Pickups page to view up-to-date cutoff times for UPS, FedEx, Purolator, Canpar, Loomis, GLS, and Canada Post. Tailored to your postal code, so you can plan your shipments with precision.',
    image: require('@/assets/images/tips/tip-image-4.png'),
  },
  {
    id: 5,
    title: 'Full visibility into every shipment',
    shortDescription: 'Track locations, timestamps, carrier contacts, and the complete shipment timeline\u2014all in one place.',
    expandedDescription:
      'Follow any shipment from pickup to delivery through a detailed scan timeline. Access locations, timestamps, and carrier contacts at a glance, so you always know exactly where a package stands.',
    image: require('@/assets/images/tips/tip-image-5.png'),
  },
];
/* eslint-enable @typescript-eslint/no-var-requires */

const TIP_STORAGE_KEY = 'productivityTip';
const TIP_ROTATION_MS = 10 * 60 * 1000; // 10 minutes

interface StoredTip {
  tipId: number;
  timestamp: number;
}

function selectNewTip(previousId: number | null): ProductivityTip {
  const candidates = previousId !== null
    ? PRODUCTIVITY_TIPS.filter((t) => t.id !== previousId)
    : PRODUCTIVITY_TIPS;

  const tip = candidates[Math.floor(Math.random() * candidates.length)];

  try {
    const entry: StoredTip = { tipId: tip.id, timestamp: Date.now() };
    sessionStorage.setItem(TIP_STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // sessionStorage write failed — tip still works for this page load
  }

  return tip;
}

export function getSessionTip(): ProductivityTip {
  try {
    const stored = sessionStorage.getItem(TIP_STORAGE_KEY);

    if (stored) {
      const parsed: StoredTip = JSON.parse(stored);
      const elapsed = Date.now() - parsed.timestamp;

      if (elapsed < TIP_ROTATION_MS) {
        const tip = PRODUCTIVITY_TIPS.find((t) => t.id === parsed.tipId);
        if (tip) return tip;
      }

      // Time expired — pick a new tip different from the previous one
      return selectNewTip(parsed.tipId);
    }
  } catch {
    // sessionStorage unavailable or corrupted — fall through
  }

  return selectNewTip(null);
}
